export type LeadMergeArea = 'platform' | 'tenant';

export type LeadMergeRelatedCounts = {
  notes: number;
  activities: number;
  tasks: number;
  communications: number;
  notifications: number;
  applicants: number;
};

export type LeadMergePreviewLead = {
  _id: string;
  fullName: string;
  email?: string;
  phoneNumber: string;
  preferredDestination?: string;
  leadSource?: string;
  statusId: string;
  status: { _id: string; code: string; name: string };
  subStatusId?: string;
  subStatus?: { _id: string; name: string };
  ownerUserId?: string;
  owner?: { userId: string; displayName: string };
  convertedApplicantId?: string;
  hasApplicant: boolean;
  relatedCounts: LeadMergeRelatedCounts;
  updatedAt?: string;
  createdAt?: string;
  profilePhotoUrl?: string;
  [key: string]: unknown;
};

export type LeadMergeFieldAutoResolved = {
  kind: 'autoResolved';
  field: string;
  value: string | number | null;
  sourceLeadId: string;
};

export type LeadMergeFieldConflict = {
  kind: 'conflict';
  field: string;
  valuesByLeadId: Record<string, string | number | null>;
};

export type LeadMergeFieldClassification = LeadMergeFieldAutoResolved | LeadMergeFieldConflict;

export type LeadMergeWarning = { code: string; message: string };
export type LeadMergeBlocker = { code: string; message: string };

export type LeadMergePreview = {
  leads: LeadMergePreviewLead[];
  defaultMasterLeadId: string;
  fieldMatrix: LeadMergeFieldClassification[];
  conflicts: LeadMergeFieldConflict[];
  autoResolved: LeadMergeFieldAutoResolved[];
  warnings: LeadMergeWarning[];
  blockers: LeadMergeBlocker[];
  relatedCountsTotal: LeadMergeRelatedCounts;
};

export type LeadMergeExecuteResult = {
  lead: { _id: string; fullName: string };
  mergeId: string;
  mergedSourceIds: string[];
  relatedMovedCounts: LeadMergeRelatedCounts & { applicantRePointed: boolean };
  idempotentReplay: boolean;
};

export const LEAD_MERGE_FIELD_LABELS: Record<string, string> = {
  fullName: 'Full name',
  phoneNumber: 'Phone',
  email: 'Email',
  whatsappNumber: 'WhatsApp',
  city: 'City',
  state: 'State',
  country: 'Country',
  preferredDestination: 'Destination',
  preferredCourse: 'Preferred course',
  intake: 'Intake',
  budgetRange: 'Budget',
  qualification: 'Qualification',
  percentageOrCgpa: 'Percentage / CGPA',
  passingYear: 'Passing year',
  institution: 'Institution',
  passportStatus: 'Passport status',
  englishTestStatus: 'English test',
  loanRequired: 'Loan required',
  stream: 'Stream',
  section: 'Section',
  message: 'Message',
  leadSource: 'Lead source',
  sourcePage: 'Source page',
  campaignName: 'Campaign',
  adSetName: 'Ad set',
  utmSource: 'UTM source',
  utmMedium: 'UTM medium',
  utmCampaign: 'UTM campaign',
  utmTerm: 'UTM term',
  utmContent: 'UTM content',
  financeTotalAmount: 'Finance total',
  financePaidAmount: 'Finance paid',
  financePendingAmount: 'Finance pending',
  statusId: 'Status',
  subStatusId: 'Sub-status',
  ownerUserId: 'Owner',
  profilePhoto: 'Profile photo',
};

/** User-facing prompt for choosing a conflict value to keep. */
export function mergeConflictChoicePrompt(field: string): string {
  const prompts: Record<string, string> = {
    fullName: 'Choose the name to keep',
    phoneNumber: 'Choose the phone number to keep',
    email: 'Choose the email to keep',
    whatsappNumber: 'Choose the WhatsApp number to keep',
    preferredDestination: 'Choose the destination to keep',
    preferredCourse: 'Choose the preferred course to keep',
    intake: 'Choose the intake to keep',
    leadSource: 'Choose the lead source to keep',
    statusId: 'Choose the status to keep',
    subStatusId: 'Choose the sub-status to keep',
    ownerUserId: 'Choose the owner to keep',
    profilePhoto: 'Choose the profile photo to keep',
    city: 'Choose the city to keep',
    state: 'Choose the state to keep',
    country: 'Choose the country to keep',
    message: 'Choose the message to keep',
    financeTotalAmount: 'Choose the finance total to keep',
    financePaidAmount: 'Choose the finance paid amount to keep',
    financePendingAmount: 'Choose the finance pending amount to keep',
  };
  if (prompts[field]) return prompts[field];
  const label = (LEAD_MERGE_FIELD_LABELS[field] ?? field).toLowerCase();
  return `Choose the ${label} to keep`;
}

export function formatMergeFieldValue(
  field: string,
  value: string | number | null,
  leads: LeadMergePreviewLead[],
): string {
  if (value === null || value === undefined || value === '') return '—';

  if (field === 'statusId') {
    const lead = leads.find((item) => item.statusId === String(value));
    return lead?.status?.name || String(value);
  }
  if (field === 'subStatusId') {
    const lead = leads.find((item) => item.subStatusId === String(value));
    return lead?.subStatus?.name || String(value);
  }
  if (field === 'ownerUserId') {
    const lead = leads.find((item) => item.ownerUserId === String(value));
    return lead?.owner?.displayName || String(value);
  }
  if (field === 'profilePhoto') {
    try {
      const parsed = JSON.parse(String(value)) as { profilePhotoUrl?: string };
      return parsed.profilePhotoUrl ? 'Has photo' : '—';
    } catch {
      return 'Has photo';
    }
  }
  return String(value);
}
